'use client';

import { Stack, Typography } from '@mui/material';
import type { JobTemplate } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { MainDialog } from '@/components/dialogs/mainDialog';
import { useDAL } from '@/contexts/useDAL';

interface DeleteJobTemplateDialogProps {
  template: JobTemplate;
  workspaceId: string;
  open: boolean;
  onClose: () => void;
  onDeleted: () => void;
}

export const DeleteJobTemplateDialog = ({
  template,
  workspaceId,
  open,
  onClose,
  onDeleted,
}: DeleteJobTemplateDialogProps) => {
  const dal = useDAL();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleClose = () => {
    setError(null);
    onClose();
  };

  const handleDelete = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await dal.lakehouse.deleteJobTemplate(workspaceId, template.id);
      onDeleted();
      handleClose();
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to delete job template'
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <MainDialog
      isOpen={open}
      isLoading={isLoading}
      header={{
        title: 'Delete Job Template',
        onClose: handleClose,
      }}
      footer={{
        primaryButton: {
          text: 'Delete',
          onClick: handleDelete,
          'data-testid': 'delete-job-template-btn',
          disabled: isLoading,
        },
        secondaryButton: {
          text: 'Cancel',
          onClick: handleClose,
          'data-testid': 'cancel-delete-job-template-btn',
        },
      }}
    >
      <Stack gap='16px' padding='0 40px'>
        <Typography variant='body1'>
          Are you sure you want to delete the job template{' '}
          <strong>&quot;{template.name}&quot;</strong>?
        </Typography>
        <Typography variant='body2' color='text.secondary'>
          This action cannot be undone. Any references to this template will no
          longer work.
        </Typography>

        {error && (
          <Typography color='error' variant='body2'>
            {error}
          </Typography>
        )}
      </Stack>
    </MainDialog>
  );
};
